/** Fixed standard colors for AutoCAD Color Index values 1–9. */
const ACI_STANDARD: Record<number, string> = {
  1: '#FF0000',
  2: '#FFFF00',
  3: '#00FF00',
  4: '#00FFFF',
  5: '#0000FF',
  6: '#FF00FF',
  8: '#808080',
  9: '#BFBFBF',
};

const ACI_GRAY = ['#333333', '#5B5B5B', '#848484', '#ADADAD', '#D6D6D6', '#FFFFFF'];
const ACI_VALUE_LEVELS = [255, 189, 129, 104, 79];

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const to = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/**
 * Converts an AutoCAD Color Index to a hex string. Returns null for ByBlock,
 * foreground white/black, and ByLayer so the caller can provide its context.
 */
export function aciToHex(aci: number): string | null {
  const i = Math.abs(Math.round(aci));
  if (i === 0 || i === 7 || i === 256) return null;
  if (i >= 1 && i <= 9) return ACI_STANDARD[i] ?? null;
  if (i >= 250 && i <= 255) return ACI_GRAY[i - 250];
  if (i >= 10 && i <= 249) {
    const n = i - 10;
    const hue = Math.floor(n / 10) * 15;
    const k = n % 10;
    const value = ACI_VALUE_LEVELS[Math.floor(k / 2)] / 255;
    const saturation = k % 2 === 0 ? 1 : 1 / 3;
    return hsvToHex(hue, saturation, value);
  }
  return null;
}

/**
 * Keeps rasterized DXF colors readable after alpha-compositing onto the light
 * simulation floor: slightly darker, with restrained additional saturation.
 */
export function enhanceSimulationRasterColor(hex: string): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const value = Number.parseInt(match[1], 16);
  const channels = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
  const max = Math.max(...channels);
  const min = Math.min(...channels);
  const saturation = max === 0 ? 0 : (max - min) / max;
  const luminance = channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114;
  // Preserve full vibrancy for technical accent layers (cyan, red, yellow, green)
  const dimFactor = saturation > 0.25 ? 1.0 : 0.88;
  const adjusted = channels.map((channel) =>
    Math.max(0, Math.min(255, Math.round((luminance + (channel - luminance) * 1.18) * dimFactor)))
  );
  return `#${adjusted.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

